'use client';

// react
import React, { useEffect, useMemo, useRef, memo } from 'react';
// mui
import {
  Button as MuiButton,
  IconButton,
  type ButtonProps as MuiButtonProps,
} from '@mui/material';

// components
import { Icon, Icons, IconType } from '@/components/atoms/Icon';

const variantMap: Record<
  ButtonVariant,
  NonNullable<MuiButtonProps['variant']>
> = {
  primary: 'contained',
  secondary: 'outlined',
  link: 'text',
};

const sizeMap: Record<ButtonSize, NonNullable<MuiButtonProps['size']>> = {
  sm: 'small',
  md: 'medium',
  lg: 'large',
};

const iconSizeMap: Record<ButtonSize, number> = {
  sm: 12,
  md: 16,
  lg: 20,
};

export type ButtonVariant = 'primary' | 'secondary' | 'link';
export type ButtonType = 'button' | 'submit' | 'reset';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps {
  className?: string;
  children?: React.ReactNode;
  size?: ButtonSize;
  variant?: ButtonVariant;
  isRectangle?: boolean;
  isDisabled?: boolean;
  isInline?: boolean;
  isFullWidth?: boolean;
  tooltip?: string;
  onPress?: () => void;
  type?: ButtonType;
  ariaLabel: string;
  iconLeft?: Icons;
  iconRight?: Icons;
  iconType?: IconType;
  to?: string;
  isDarkMode?: boolean;
  isPreventDefault?: boolean;
  isNoPadding?: boolean;
  target?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Button allows the user to trigger an action or event, such as submitting a
 * form, opening a dialog, canceling an action, or performing a delete
 * operation.
 */
export const Button = ({
  className,
  size = 'md',
  variant = 'primary',
  isDisabled = false,
  isRectangle = false,
  isInline = false,
  isFullWidth = false,
  type = 'button',
  iconLeft,
  iconRight,
  iconType,
  ariaLabel,
  children,
  isDarkMode,
  to,
  target,
  isPreventDefault,
  isNoPadding,
  onClick,
  onPress,
  ...props
}: ButtonProps) => {
  // state
  const [isIconOnly, setIsIconOnly] = React.useState(false);

  // memos
  const commonProps = useMemo(() => {
    return {
      onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
        if (isPreventDefault) {
          e.preventDefault();
          e.stopPropagation();
        }
        onClick?.(e);
        onPress?.();
      },
      target: target,
      'aria-label': ariaLabel,
      disabled: isDisabled,
    };
  }, [isPreventDefault, onClick, onPress, target, ariaLabel, isDisabled]);

  const IconButtonWrapper = memo(
    ({
      className,
      to,
      children,
    }: {
      className?: string;
      to?: string;
      children: React.ReactNode;
    }) => {
      return to ? (
        <a
          className={className}
          href={to}
          target={target}
          style={{ textDecoration: 'none' }}
        >
          {children}
        </a>
      ) : (
        <div className={className}>{children}</div>
      );
    },
  );

  // effects
  useEffect(() => {
    setIsIconOnly(!!(iconLeft && !children && !iconRight));
  }, [iconLeft, children, iconRight]);

  return !isIconOnly ? (
    <MuiButton
      {...commonProps}
      className={className}
      type={type}
      {...(to ? { component: 'a', href: to } : {})}
      variant={variantMap[variant]}
      color={isDarkMode ? 'secondary' : 'primary'}
      size={sizeMap[size]}
      disableRipple={variant === 'link'}
      disableTouchRipple={variant === 'link'}
      style={{
        width: isFullWidth ? '100%' : 'auto',
        display: isInline ? 'inline-flex' : 'flex',
        textDecoration: variant !== 'link' ? 'none' : undefined,
      }}
      startIcon={
        iconLeft ? (
          <Icon icon={iconLeft} type={iconType} fontSize={iconSizeMap[size]} />
        ) : undefined
      }
      endIcon={
        iconRight ? (
          <Icon icon={iconRight} type={iconType} fontSize={iconSizeMap[size]} />
        ) : undefined
      }
    >
      {children}
    </MuiButton>
  ) : (
    <IconButtonWrapper to={to} className={className}>
      <IconButton {...commonProps} size={sizeMap[size]}>
        {iconLeft && (
          <Icon icon={iconLeft} type={iconType} fontSize={iconSizeMap[size]} />
        )}
      </IconButton>
    </IconButtonWrapper>
  );
};

Button.componentName = 'Button';

export default Button;
